import * as angular from 'angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { AppStore } from '~/types/main.types';

import { FeatureFlagHelpers } from '../common/feature-flag-helpers';
import featureIsEnabled = FeatureFlagHelpers.featureIsEnabled;
import { Features } from '@mode/legacy/types-app-state';
import { FeatureFlagStateType } from '@mode/legacy/types-app-state';
import { FeatureSelectors } from '../selectors/feature.selectors';
import getEnabledFeatures = FeatureSelectors.getEnabledFeatures;

import { MSG_TYPES, WindowMessageService } from './window-message-service';

/**
 * Feature Toggle Service
 *
 * A service that runs outside the embed iframe and
 * within it.  Depending on it's context it will merge the
 * app state from postMessage or directly from the store.
 * This allows us to expose observables that are agnostic
 * to the source of their state and thus can be used app-wide
 * @author Franklin <franklin@modeanalytics.com>
 */
export class FeatureToggleService {
  private store: AppStore;
  private enabledFlagsSource: BehaviorSubject<FeatureFlagStateType[] | null>;
  private enabledFlags$: Observable<FeatureFlagStateType[] | null>;
  public featureDrilldownEnabled$: Observable<boolean>;
  public featureExploreEnabled$: Observable<boolean>;
  public featureVizToolbarEnabled$: Observable<boolean>;
  public featureChartDataExportEnabled$: Observable<boolean>;
  public featureBusinessUpsellEnabled$: Observable<boolean>;
  public featureModeStudioEnabled$: Observable<boolean>;
  public featureIntercomChatEnabled$: Observable<boolean>;
  public featureParameterRunButtonEnabled$: Observable<boolean>;
  public featureParametersEnabled$: Observable<boolean>;

  static $inject = ['$injector', '$window', 'WindowMessageService'];

  constructor(
    private $injector: angular.auto.IInjectorService,
    private $window: angular.IWindowService,
    private wms: WindowMessageService
  ) {
    this.enabledFlagsSource = new BehaviorSubject(null);
    // conditionally load for apps that do not have an rx store
    if ($injector.has('RxStoreService') && this.appIsEligible()) {
      this.store = $injector.get('RxStoreService');
    }
    this.initFeatureStateObservable();
    this.initFeatureObservables();
    this.whenIframeIsReady(this.initParentToFrameLink.bind(this));
    this.whenParentFrameIsReady(this.initFrameToParentLink.bind(this));
  }

  appIsEligible() {
    const appName = this.$window.document.body.getAttribute('mode-app');

    return ['mode.report', 'mode.editor'].includes(appName);
  }

  /**
   * Create enabled-flags-observable either by merging
   * an observable of the {@link FeatureSelectors.getEnabledFeatures}
   * selector or an observable on the {@link FeatureToggleService.enabledFlagsSource}
   */
  initFeatureStateObservable(): void {
    if (this.store) {
      this.enabledFlags$ = this.enabledFlagsSource.pipe(mergeMap(() => this.store.select(getEnabledFeatures)));
    } else {
      this.enabledFlags$ = this.enabledFlagsSource.asObservable();
    }
  }

  /**
   * Generate individual feature observables
   */
  initFeatureObservables(): void {
    this.featureDrilldownEnabled$ = this.enabledFlags$.pipe(
      map((state) => featureIsEnabled(state, Features.Report.DrillDown))
    );
    this.featureExploreEnabled$ = this.enabledFlags$.pipe(
      map((state) => featureIsEnabled(state, Features.Report.Explore))
    );
    this.featureVizToolbarEnabled$ = this.enabledFlags$.pipe(
      map((state) => featureIsEnabled(state, Features.Report.VizToolbar))
    );
    this.featureChartDataExportEnabled$ = this.enabledFlags$.pipe(
      map((state) => featureIsEnabled(state, Features.Report.ChartDataExport))
    );
    this.featureBusinessUpsellEnabled$ = this.enabledFlags$.pipe(
      map((state) => featureIsEnabled(state, Features.Common.ModeBusinessUpsell))
    );
    this.featureModeStudioEnabled$ = this.enabledFlags$.pipe(
      map((state) => featureIsEnabled(state, Features.Common.ModeStudio))
    );
    this.featureIntercomChatEnabled$ = this.enabledFlags$.pipe(
      map((state) => featureIsEnabled(state, Features.Common.IntercomChat))
    );
    this.featureParameterRunButtonEnabled$ = this.enabledFlags$.pipe(
      map((state) => featureIsEnabled(state, Features.Report.ParameterRunButton))
    );
    this.featureParametersEnabled$ = this.enabledFlags$.pipe(
      map((state) => featureIsEnabled(state, Features.Report.Parameters))
    );
  }

  /**
   * Execute callback when the embedded iframe notifies
   * this top/parent frame that it is ready to receive
   * data
   * @param {Function} callback
   */
  whenIframeIsReady(callback: Function): void {
    this.$window.addEventListener('message', ({ data }) => {
      const frameIsReady = data && data.name && data.name === 'ready';

      if (this.isTopFrame() && frameIsReady) {
        callback();
      }
    });
  }

  /**
   * If we are in the embed iframe execute callback
   * @param {Function} callback
   */
  whenParentFrameIsReady(callback: Function): void {
    if (!this.isTopFrame()) {
      callback();
    }
  }

  isTopFrame(): boolean {
    return !!this.$window.frames['mode-embed-frame'] && !!this.store;
  }

  /**
   * Subscribe to feature state and post to iframe
   * upon store update. Note that this update will be "caught"
   * by the {@link FeatureToggleService} in the embedded iframe which
   * will sub-sequentially update it's local {@link FeatureToggleService.enabledFlagsSource}
   */
  initParentToFrameLink(): void {
    if (this.isTopFrame()) {
      this.store.select(getEnabledFeatures).subscribe((features) => this.wms.updateFeatureState(features));
    }
  }

  /**
   * Update local state source upon {@link MSG_TYPES.UpdateFeatureState}
   * post message from the parent frame
   */
  initFrameToParentLink(): void {
    this.wms.subscribe(this, (message) => this.updateLocalState(message['state']), MSG_TYPES.UpdateFeatureState);
  }

  /**
   * Manually update local state source
   * @param {FeatureFlagStateType[]} state
   */
  updateLocalState(state: FeatureFlagStateType[]): void {
    if (!this.isTopFrame()) {
      this.enabledFlagsSource.next(state);
    }
  }
}
